import { useEffect, useState, FormEvent } from 'react';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { Help } from '../components/Help';
import { BulkImportModal } from '../components/BulkImportModal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  listPlant,
  createPlant,
  updatePlant,
  archivePlant,
  type PlantItem,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';

interface Draft {
  name: string;
  serial: string;
  loler_date: string;
  pat_date: string;
  notes: string;
}

const EMPTY: Draft = { name: '', serial: '', loler_date: '', pat_date: '', notes: '' };

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function RegisterPlant() {
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [items, setItems] = useState<PlantItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlantItem | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  async function refresh() {
    if (!org) return;
    setLoading(true);
    try {
      setItems(await listPlant(org.org_id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load — stale-while-revalidate. Post-mutation refreshes
  // still call refresh() directly for network-only behaviour.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    cacheListByOrg<PlantItem>('plant', org.org_id).then((cached) => {
      if (cancelled) return;
      if (cached.length > 0) {
        haveCache = true;
        setItems(cached);
        setLoading(false);
      }
    }).catch(() => {});
    listPlant(org.org_id).then((list) => {
      if (cancelled) return;
      setItems(list);
      setError(null);
    }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [org]);

  function startAdd() {
    setEditing(null);
    setDraft(EMPTY);
    setShowAdd(true);
  }

  function startEdit(item: PlantItem) {
    setEditing(item);
    setDraft({
      name: item.name,
      serial: item.serial ?? '',
      loler_date: item.loler_date ?? '',
      pat_date: item.pat_date ?? '',
      notes: item.notes ?? '',
    });
    setShowAdd(true);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    if (!draft.name.trim()) { setError('Name is required.'); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await updatePlant(editing.id, {
          name: draft.name.trim(),
          serial: draft.serial.trim() || null,
          loler_date: draft.loler_date || null,
          pat_date: draft.pat_date || null,
          notes: draft.notes.trim() || null,
        });
      } else {
        await createPlant({
          orgId: org.org_id,
          name: draft.name.trim(),
          serial: draft.serial.trim() || null,
          loler_date: draft.loler_date || null,
          pat_date: draft.pat_date || null,
          notes: draft.notes.trim() || null,
        });
      }
      setShowAdd(false);
      setEditing(null);
      setDraft(EMPTY);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onArchive(item: PlantItem) {
    if (!(await confirm({
      title: `Archive "${item.name}"?`,
      body: 'It\'ll be hidden from the register. Inspection history stays on file.',
      confirmLabel: 'Archive',
      danger: true,
    }))) return;
    try {
      await archivePlant(item.id);
      setShowAdd(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppShell title="Plant register" back="/registers">
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Owned plant and tools. Update LOLER (lifting) and PAT dates here.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" onClick={startAdd}>+ Add plant</button>
        <button type="button" className="btn btn-ghost" onClick={() => setBulkOpen(true)}>Bulk import (CSV)</button>
      </div>

      {loading ? (
        <LoadingIndicator />
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>No plant logged yet.</p>
        </div>
      ) : (
        <ul className="person-list">
          {items.map((p) => (
            <li key={p.id}>
              <button type="button" className="site-item-link" onClick={() => startEdit(p)} style={{ width: '100%', textAlign: 'left', font: 'inherit' }}>
                <div>
                  <p className="site-name">{p.name}</p>
                  <p className="site-meta">
                    {p.serial ? `${p.serial} · ` : ''}
                    LOLER {fmtDate(p.loler_date)} · PAT {fmtDate(p.pat_date)}
                  </p>
                </div>
                <span className="chev">›</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={showAdd}
        title={editing ? 'Edit plant' : 'Add plant'}
        onClose={() => { setShowAdd(false); setEditing(null); }}
      >
        <form onSubmit={onSave}>
          <div className="field"><label htmlFor="plant-name">Item / model *</label><input id="plant-name" type="text" required placeholder="e.g. JCB 540-170 telehandler" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus /></div>
          <div className="field"><label htmlFor="plant-serial">Serial / reg</label><input id="plant-serial" type="text" placeholder="e.g. JCB540170-2024-0019" value={draft.serial} onChange={(e) => setDraft({ ...draft, serial: e.target.value })} /></div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="plant-loler">Last LOLER <Help>Lifting kit cert — telehandlers, slings, hoists. Annual.</Help></label>
              <input id="plant-loler" type="date" value={draft.loler_date} onChange={(e) => setDraft({ ...draft, loler_date: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="plant-pat">Last PAT <Help>Electrical test — leads, transformers, power tools. Annual.</Help></label>
              <input id="plant-pat" type="date" value={draft.pat_date} onChange={(e) => setDraft({ ...draft, pat_date: e.target.value })} />
            </div>
          </div>
          <div className="field"><label htmlFor="plant-notes">Notes</label><textarea id="plant-notes" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>

          <div className="btn-stack">
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
            {editing && <button type="button" className="btn btn-ghost" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => onArchive(editing)}>Archive</button>}
            <button type="button" className="btn btn-ghost" onClick={() => { setShowAdd(false); setEditing(null); }}>Cancel</button>
          </div>
        </form>
      </Modal>
    <BulkImportModal
        open={bulkOpen}
        title="Bulk import plant"
        columns={[
          { key: 'name',  label: 'Name',         required: true,  hint: 'e.g. JCB 540-170 telehandler' },
          { key: 'serial',label: 'Serial',                          hint: 'Optional. Manufacturer serial / fleet ref.' },
          { key: 'loler', label: 'Last LOLER',                      hint: 'YYYY-MM-DD. Lifting cert.' },
          { key: 'pat',   label: 'Last PAT',                        hint: 'YYYY-MM-DD. Electrical test.' },
          { key: 'notes', label: 'Notes',                           hint: 'Optional.' },
        ]}
        exampleRows={[
          'name,serial,loler,pat,notes',
          'JCB 540-170,JCB540170-2024-0019,2025-09-01,2025-12-01,Hire fleet',
          'Bosch GBH 36V,GBH36-7741,,2024-08-15,Cordless SDS',
        ]}
        toInput={(row) => row.name?.trim() ? ({
          orgId: org!.org_id,
          name: row.name.trim(),
          serial: row.serial?.trim() || null,
          loler_date: row.loler?.trim() || null,
          pat_date: row.pat?.trim() || null,
          notes: row.notes?.trim() || null,
        }) : null}
        createRow={async (input) => { await createPlant(input); }}
        onClose={() => setBulkOpen(false)}
        onImported={async () => { setBulkOpen(false); await refresh(); }}
      />
    </AppShell>
  );
}
